import clsx from "clsx";
import { motion } from "framer-motion";
import { ETransactionType } from "src/helpers/constants";
import { CSSProperties, memo, ReactElement, useMemo, useState } from "react";
import useAllTransactions from "src/hooks/useAllTransactions";
import useCurrentUser from "src/hooks/useCurrentUser";
import useMobile from "src/hooks/useMobile";
import { useTranslation } from "react-i18next";
import useCurrencies from "src/hooks/useCurrencies";
import ShinyCard from "../BankCard/ShinyCard";
import useUiPreferences from "src/store/useUiPreferences";
import CheckBox from "../CheckBox/CheckBox";
import useColors from "src/hooks/useColors";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { ICON_SIZES } from "src/constants/icons";
import { IButton } from "../Button/Button.types";
import {
  MdArrowDownward,
  MdArrowUpward,
  MdOutlineSavings,
} from "react-icons/md";
import { formatCurrency } from "src/helpers/utils";
import { forceIconSize } from "src/helpers/icon-utils";
import SmartImage from "../SmartImage/SmartImage";
import CurrencyBadge from "../CurrencyBadge/CurrencyBadge";
import ClientProviderInfoModal from "../ClientProviderInfoModal/ClientProviderInfoModal";
import useModalsStore from "src/store/modalsStore";

export enum EStatsSummaryCardDataType {
  CLIENT_PROVIDER = "client-provider",
  MONTH = "month",
  DAY = "day",
  CATEGORY = "category",
  OTHER = "NONE",
}

interface IStatsSummaryCard {
  title?: string;
  count?: number;
  income?: number;
  expenses?: number;
  difference?: number;
  index: number;
  customAnimationDirection?: "x" | "y" | null;
  onSelect?: ({
    transaction,
    checked,
  }: {
    transaction: any;
    checked: boolean;
  }) => void;
  checked?: boolean;
  onClick?: (e: any) => void;
  buttons?: IButton[];
  icon?: ReactElement;
  image?: string;
  colors?: any;
  showIncome?: boolean;
  showExpenses?: boolean;
  showBalance?: boolean;
  currencySymbol: string;
  dataType: EStatsSummaryCardDataType;
  id: string;
}

const StatsSummaryCard: React.FC<IStatsSummaryCard> = ({
  title,
  count,
  income,
  expenses,
  difference,
  index,
  customAnimationDirection = null,
  onSelect = null,
  checked = false,
  onClick,
  buttons = [],
  icon = null,
  image = null,
  colors = {},
  showIncome = true,
  showExpenses = true,
  showBalance = true,
  currencySymbol,
  dataType = EStatsSummaryCardDataType.OTHER,
  id,
}) => {
  const {
    setShowEditCategoryModal,
    setShowDayColorsModal,
    setShowMonthColorsModal,
  } = useModalsStore();

  const enableAnimations = useUiPreferences(
    (state) => state.misc.enableComplexAnimations,
  );
  const { getIncomeColors, getExpensesColors, getBalanceColors } = useColors();
  const { t } = useTranslation(["common", "misc"]);
  const { getCurrencyDataByCode } = useCurrencies();
  const { isMobile } = useMobile();
  const { getSourceCustomSettings, getCategoryCustomSettings } =
    useAllTransactions();
  const [showSourceModal, setShowSourceModal] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [showRecurringSettings, setShowRecurringSettings] =
    useState<boolean>(false);
  const { getAccountById } = useCurrentUser();

  const animationDirection = useMemo(() => {
    return customAnimationDirection
      ? customAnimationDirection
      : isMobile
        ? "y"
        : "x";
  }, [isMobile]);

  const getCheckboxStyle = (transactionType: ETransactionType) => {
    switch (transactionType) {
      case ETransactionType.INCOME: {
        return {
          color: getIncomeColors().string.background,
        };
      }
      case ETransactionType.EXPENSE: {
        return {
          color: getExpensesColors().string.background,
        };
      }
      default: {
        return {};
      }
    }
  };

  const columnsToShow = useMemo(() => {
    const result = [];

    if (showIncome) result.push(1);
    if (showExpenses) result.push(1);
    if (showBalance) result.push(1);

    return result.length;
  }, [showIncome, showExpenses, showBalance]);

  return (
    <>
      <motion.div
        className={clsx([
          "flex",
          "w-full",
          "group",
          "bg-gray-50 dark:bg-gray-900/30",
          "p-2",
          "md:p-2",
          "rounded",
          "hover:rounded",
          "border-0",
          "border-gray-50",
          "dark:border-gray-900/50",
          "hover:bg-gray-100",
          "hover:dark:bg-gray-900",
        ])}
        {...(enableAnimations && {
          initial: {
            opacity: 0,
            [animationDirection]: index % 2 === 0 ? -100 : 100,
          },
          animate: { opacity: 1, [animationDirection]: 0 },
          transition: {
            duration: 0.2,
            ease: "easeOut",
            // delay: index * 0.1,
          },
        })}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "md:gap-2",
            "gap-2",
            {
              "cursor-pointer": !!onClick,
            },
          ])}
          onClick={(e: any) => {
            onClick?.(e);
          }}
        >
          <div
            className={clsx([
              "flex",
              "flex-row",
              "gap-2",
              "md:gap-2",
              "items-center",
              "rounded",
              "p-2",
            ])}
            style={colors}
          >
            <div
              className={clsx([
                "!h-[50px]",
                "!w-[50px]",
                "!min-h-[50px]",
                "!min-w-[50px]",
                "bg-checkers",
                "md:!h-[50px]",
                "md:!w-[50px]",
                "md:!min-h-[50px]",
                "md:!min-w-[50px]",
                "rounded-full",
                "overflow-hidden",
                "inline-flex",
                "items-center",
                "justify-center",
                "relative",
                {
                  "cursor-pointer":
                    dataType !== EStatsSummaryCardDataType.OTHER,
                },
              ])}
              title={t("common:tooltips.contact_information")}
              onClick={(e: any) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();

                if (dataType === EStatsSummaryCardDataType.CLIENT_PROVIDER) {
                  setShowSourceModal(true);
                }

                if (dataType === EStatsSummaryCardDataType.CATEGORY) {
                  const { bg, color, icon } = getCategoryCustomSettings(id);
                  setShowEditCategoryModal(true, {
                    name: id,
                    background: bg,
                    color: color,
                    icon: icon,
                  });
                }

                if (dataType === EStatsSummaryCardDataType.DAY) {
                  setShowDayColorsModal(true);
                }

                if (dataType === EStatsSummaryCardDataType.MONTH) {
                  setShowMonthColorsModal(true);
                }
              }}
            >
              <ShinyCard className={clsx(["w-full", "h-full"])}>
                {icon || image ? (
                  <>
                    {image ? (
                      <SmartImage
                        src={image}
                        className={clsx(["bg-checkers", "!w-full", "!h-full"])}
                        alt={title}
                      />
                    ) : (
                      <div
                        className={clsx([
                          "flex",
                          "!h-full",
                          "!w-full",
                          "items-center",
                          "justify-center",
                        ])}
                      >
                        {forceIconSize({
                          icon,
                          size: isMobile ? ICON_SIZES.MD : ICON_SIZES.MD,
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div>Nada</div>
                )}
              </ShinyCard>
            </div>
            <div className={clsx(["flex", "flex-col", "w-full", "truncate"])}>
              <div
                className={clsx([
                  "text-xs",
                  "md:text-xs",
                  "truncate",
                  "font-medium",
                ])}
              >
                {title}
              </div>
              <div className={clsx(["text-xs", "md:text-xs", "truncate"])}>
                Transacciones: {count}
              </div>
            </div>
            {buttons?.length > 0 && (
              <div>
                <ButtonGroup buttons={buttons} />
              </div>
            )}
          </div>
          <div
            className={clsx([
              "grid",
              `[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]`,
              `md:[grid-template-columns:repeat(var(--cols-md),minmax(0,1fr))]`,
              // "grid-cols-3",
              // "justify-center",
              // "items-end",
            ])}
            style={
              {
                "--cols": columnsToShow,
                "--cols-md": columnsToShow,
              } as CSSProperties
            }
            onClick={(e: any) => {
              if (onSelect) {
                e?.stopPropagation?.();
                e?.preventDefault?.();
                // onSelect?.({
                //   transaction,
                //   checked: !checked,
                // });
              }
            }}
          >
            {onSelect ? (
              <CheckBox
                checked={checked}
                onChange={(value: boolean) => {
                  // onSelect?.({
                  //   transaction,
                  //   checked: value,
                  // });
                }}
                // iconStyles={getCheckboxStyle(transaction.type)}
              />
            ) : null}
            {showIncome && (
              <div
                className={clsx([
                  "flex",
                  {
                    "flex-col": columnsToShow > 1,
                    "flex-row gap-2 items-center": columnsToShow === 1,
                  },
                ])}
              >
                <MdArrowDownward
                  size={ICON_SIZES.SM}
                  style={{
                    color: getIncomeColors().string.background,
                  }}
                />
                <div className={clsx(["text-xs"])}>
                  {t("misc:summary.income")}
                </div>
                <div className={clsx(["font-medium", "text-xs", "flex"])}>
                  <CurrencyBadge
                    kind={ETransactionType.INCOME}
                    value={income || 0}
                    currencySymbol={currencySymbol}
                    showIcon={false}
                  />
                </div>
              </div>
            )}
            {showExpenses && (
              <div
                className={clsx([
                  "flex",
                  {
                    "flex-col": columnsToShow > 1,
                    "flex-row gap-2 items-center": columnsToShow === 1,
                  },
                ])}
              >
                <MdArrowUpward
                  size={ICON_SIZES.SM}
                  style={{
                    color: getExpensesColors().string.background,
                  }}
                />
                <div className={clsx(["text-xs"])}>
                  {t("misc:summary.expenses")}
                </div>
                <div className={clsx(["font-medium", "text-xs", "flex"])}>
                  <CurrencyBadge
                    kind={ETransactionType.EXPENSE}
                    value={expenses || 0}
                    currencySymbol={currencySymbol}
                    showIcon={false}
                  />
                </div>
              </div>
            )}
            {showBalance && (
              <div
                className={clsx([
                  "flex",
                  {
                    "flex-col": columnsToShow > 1,
                    "flex-row gap-2 items-center": columnsToShow === 1,
                  },
                ])}
              >
                <MdOutlineSavings
                  size={ICON_SIZES.SM}
                  style={{
                    color:
                      (difference || 0) >= 0
                        ? getBalanceColors().string.positive.background
                        : getBalanceColors().string.negative.background,
                  }}
                />
                <div className={clsx(["text-xs"])}>
                  {t("misc:summary.diff")}
                </div>
                <div className={clsx(["font-medium", "text-xs", "flex"])}>
                  <CurrencyBadge
                    kind="DIFFERENCE"
                    value={difference || 0}
                    currencySymbol={currencySymbol}
                    showIcon={false}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {showSourceModal && (
        <ClientProviderInfoModal
          clientProvider={id}
          onClose={() => setShowSourceModal(false)}
        />
      )}
    </>
  );
};

export default memo(StatsSummaryCard);
